import { useState, useEffect, Fragment } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Loader2, CheckCircle2, XCircle, ShieldAlert, RefreshCw } from 'lucide-react';

/**
 * Owner-only reconciliation debug panel at /admin/reconcile.
 *
 * Not part of the user product surface — it's an internal trust instrument.
 * For every user in the system it shows:
 *   gross income / total expenses / net / bucket sum / invariant pass-fail
 *
 * Enforces owner role server-side (`/api/admin/reconcile` returns 403 otherwise);
 * the UI also hides itself gracefully if the API rejects.
 */
function AdminReconcile() {
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/reconcile`);
      setData(r.data);
    } catch (e) {
      if (e.response?.status === 403) {
        setForbidden(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="max-w-lg mx-auto mt-20 text-center" data-testid="admin-reconcile-forbidden">
        <ShieldAlert className="w-12 h-12 mx-auto text-amber-500 mb-3" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Owner role required</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
          This internal tool is restricted to workspace owners.
        </p>
      </div>
    );
  }

  if (!data) return null;

  const { total_users, healthy_count, unhealthy_count, users, generated_at } = data;

  return (
    <div className="space-y-6" data-testid="admin-reconcile-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Reconciliation Health</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Invariants A–D verified from the canonical engine. Generated {new Date(generated_at).toLocaleString()}.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="admin-reconcile-refresh"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Total users" value={total_users} tone="blue" />
        <StatCard label="Healthy" value={healthy_count} tone="emerald" />
        <StatCard label="Unhealthy" value={unhealthy_count} tone={unhealthy_count > 0 ? 'rose' : 'gray'} />
      </div>

      {/* User table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40 text-left text-gray-500 dark:text-gray-400 uppercase text-xs tracking-wide">
            <tr>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Email</th>
              <th className="px-4 py-2">Role</th>
              <th className="px-4 py-2 text-right">Gross income</th>
              <th className="px-4 py-2 text-right">Expenses</th>
              <th className="px-4 py-2 text-right">Net</th>
              <th className="px-4 py-2 text-right">Σ buckets</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {users.map((u) => {
              const ok = u.all_ok;
              const isOpen = expanded === u.user_id;
              return (
                <Fragment key={u.user_id}>
                  <tr
                    onClick={() => setExpanded(isOpen ? null : u.user_id)}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/30"
                    data-testid={`admin-reconcile-row-${u.user_id}`}
                  >
                    <td className="px-4 py-2">
                      {ok ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                          <CheckCircle2 className="w-4 h-4" /> OK
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-rose-600 dark:text-rose-400">
                          <XCircle className="w-4 h-4" /> Fail
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{u.email || u.user_id}</td>
                    <td className="px-4 py-2 text-gray-500 dark:text-gray-400">{u.role || '—'}</td>
                    <td className="px-4 py-2 text-right tabular-nums">${(u.gross_income ?? 0).toLocaleString()}</td>
                    <td className="px-4 py-2 text-right tabular-nums">${(u.total_expenses ?? 0).toLocaleString()}</td>
                    <td className="px-4 py-2 text-right tabular-nums">${(u.net_after_expenses ?? 0).toLocaleString()}</td>
                    <td className="px-4 py-2 text-right tabular-nums">${(u.bucket_sum ?? 0).toLocaleString()}</td>
                  </tr>
                  {isOpen && u.invariants && (
                    <tr className="bg-gray-50 dark:bg-gray-900/20">
                      <td colSpan={7} className="px-6 py-4">
                        <InvariantList invariants={u.invariants} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone }) {
  const tones = {
    blue:    'border-blue-500/30 bg-blue-500/5 text-blue-700 dark:text-blue-300',
    emerald: 'border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300',
    rose:    'border-rose-500/30 bg-rose-500/5 text-rose-700 dark:text-rose-300',
    gray:    'border-gray-400/30 bg-gray-500/5 text-gray-700 dark:text-gray-300',
  };
  return (
    <div className={`rounded-lg border px-4 py-3 ${tones[tone] || tones.gray}`}>
      <div className="text-xs uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-2xl font-semibold tabular-nums mt-1">{value}</div>
    </div>
  );
}

function InvariantList({ invariants }) {
  return (
    <ul className="space-y-1">
      {Object.entries(invariants).map(([name, v]) => (
        <li key={name} className="flex items-start gap-2 text-xs">
          {v.ok ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
          ) : (
            <XCircle className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" />
          )}
          <span className="text-gray-700 dark:text-gray-200">
            <code className="text-[11px] bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">{name}</code>
            <span className="ml-2 text-gray-500 dark:text-gray-400">{v.detail}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

export default AdminReconcile;
